import React, { useState, useEffect } from 'react';
import { X, Tag, Mic, MicOff, Bold, Italic, Underline, Folder, List, ListOrdered, CheckSquare } from 'lucide-react';
import { VoiceRecorder } from './VoiceRecorder';
import type { Note, Folder as FolderType } from '../../hooks/useNotes';
import { useTaskContext } from '../../contexts/TaskContext';
import { useSubscription } from '../../contexts/SubscriptionContext';

interface NoteEditorProps {
  initialContent?: Note['content'];
  initialTags?: string[];
  initialColor?: string;
  initialRole?: Exclude<Role, 'all'>;
  initialFolderId?: string;
  folders: FolderType[];
  onSave: (note: { 
    content: Note['content']; 
    tags: string[]; 
    color: string; 
    folderId?: string; 
    role: Exclude<Role, 'all'>;
  }) => void;
  onClose: () => void;
}

export function NoteEditor({
  initialContent = { title: '', text: '', formatting: [] },
  initialTags = [],
  initialColor = '#1A1B1E',
  initialRole = 'work',
  initialFolderId,
  folders,
  onSave,
  onClose
}: NoteEditorProps) {
  const [title, setTitle] = useState(initialContent.title);
  const [content, setContent] = useState(initialContent.text);
  const [formatting, setFormatting] = useState(initialContent.formatting);
  const [tags, setTags] = useState(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [color, setColor] = useState(initialColor);
  const [folderId, setFolderId] = useState<string | undefined>(initialFolderId);
  const [role, setRole] = useState<Exclude<Role, 'all'>>(initialRole);
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [selectedFormat, setSelectedFormat] = useState<'paragraph' | 'bullet' | 'number' | 'checkbox'>('paragraph');
  const [isRecording, setIsRecording] = useState(false);

  const { roles } = useTaskContext();
  const { hasFeature } = useSubscription();
  const isPremium = hasFeature('custom_roles');

  const handleFormat = (type: 'bold' | 'italic' | 'underline' | 'bullet' | 'number' | 'checkbox') => {
    const newFormatting = [...formatting];
    const start = Math.min(selectionStart, selectionEnd);
    const end = Math.max(selectionStart, selectionEnd);
    
    if (start !== end) {
      newFormatting.push({
        [type]: true,
        start,
        end,
      });
      setFormatting(newFormatting);
    }
  };

  const handleListFormat = (type: 'bullet' | 'number' | 'checkbox') => {
    setSelectedFormat(type);
    const lines = content.split('\n');
    const newLines = lines.map(line => {
      if (!line.trim()) return line;
      switch (type) {
        case 'bullet':
          return line.startsWith('• ') ? line : `• ${line}`;
        case 'number':
          return /^\d+\.\s/.test(line) ? line : `1. ${line}`;
        case 'checkbox':
          return line.startsWith('[ ] ') ? line : `[ ] ${line}`;
        default:
          return line;
      }
    });
    setContent(newLines.join('\n'));
  };
  const handleTagAdd = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim()) {
      const newNote = {
        content: { title, text: content, formatting },
        tags,
        color,
        folderId,
        role
      };
      
      try {
        onSave(newNote);
      } catch (error) {
        console.error('Error saving note:', error);
        // TODO: Add error handling UI
      }
    }
  };

  return (
    <div className="modal-container">
      <div className="modal-content max-w-4xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl">Add Brain Dump</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brain Dump Title"
            className="w-full px-6 py-4 bg-black/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] text-xl"
            autoFocus
          />

          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 bg-black/20 rounded-lg p-1">
              <div className="border-r border-gray-600 pr-1 mr-1">
              <VoiceRecorder 
                onTranscription={(text) => {
                  setContent(prev => prev + (prev ? '\n\n' : '') + text);
                }}
              />
              <button
                type="button"
                onClick={() => handleFormat('bold')}
                className="p-1.5 hover:bg-black/20 rounded-lg transition-colors"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleFormat('italic')}
                className="p-1.5 hover:bg-black/20 rounded-lg transition-colors"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleFormat('underline')}
                className="p-1.5 hover:bg-black/20 rounded-lg transition-colors"
              >
                <Underline className="w-4 h-4" />
              </button>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => handleListFormat('bullet')}
                  className={`p-1.5 hover:bg-black/20 rounded-lg transition-colors ${
                    selectedFormat === 'bullet' ? 'bg-black/20' : ''
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleListFormat('number')}
                  className={`p-1.5 hover:bg-black/20 rounded-lg transition-colors ${
                    selectedFormat === 'number' ? 'bg-black/20' : ''
                  }`}
                >
                  <ListOrdered className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleListFormat('checkbox')}
                  className={`p-1.5 hover:bg-black/20 rounded-lg transition-colors ${
                    selectedFormat === 'checkbox' ? 'bg-black/20' : ''
                  }`}
                >
                  <CheckSquare className="w-4 h-4" />
                </button>
              </div>
            </div>

            <select
              value={folderId || ''}
              onChange={(e) => setFolderId(e.target.value || undefined)}
              className="bg-black/20 rounded-lg px-3 py-2 text-sm [&>option]:bg-[#2A2B2E] min-w-[120px]"
            >
              <option value="">No folder</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setIsRecording(!isRecording)}
              className={`p-2 rounded-lg transition-colors ${
                isRecording ? 'bg-red-500 text-white' : 'bg-black/20 hover:bg-black/30'
              }`}
            >
              {isRecording ? (
                <MicOff className="w-5 h-5" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
            </button>
          </div>

          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onSelect={(e) => {
              const target = e.target as HTMLTextAreaElement;
              setSelectionStart(target.selectionStart);
              setSelectionEnd(target.selectionEnd); 
            }}
            placeholder="What's on your mind?"
            className="w-full px-6 py-4 bg-black/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] min-h-[calc(100vh-400px)] resize-none text-lg"
          />

          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 px-2 py-1 bg-black/30 rounded-lg text-sm"
                >
                  <Tag className="w-4 h-4" />
                  {tag}
                  <button
                    type="button"
                    onClick={() => setTags(tags.filter(t => t !== tag))}
                    className="ml-1 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagAdd}
                placeholder="Add tags (press Enter)"
                className="flex-1 bg-transparent focus:outline-none text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400">Category</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Exclude<Role, 'all'>)}
              className="w-full mt-1 bg-black/20 rounded-lg px-3 py-2 text-sm [&>option]:bg-[#2A2B2E]"
            >
              {isPremium ? (
                roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))
              ) : (
                <>
                  <option value="work">Work</option>
                  <option value="family">Family</option>
                  <option value="personal">Personal</option>
                </>
              )}
            </select>
          </div>

          <button
            type="submit"
            className="w-full bg-[var(--accent-color)] text-black rounded-xl py-2 hover:opacity-90 transition-opacity"
          >
            Save Brain Dump
          </button>
        </form>
      </div>
    </div>
  );
}